import { useState, useCallback } from 'react';
import { $authHost } from 'app/configs/axiosConfig';
import { useDropzone } from 'react-dropzone';
import { Button, IconButton, DialogActions, Divider, Icon } from '@mui/material';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';

interface FileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({ open, onClose, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>(
    'idle',
  );
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const maxSize = 10 * 1024 * 1024;

  const { enqueueSnackbar } = useSnackbar();

  const handleFileRejected = () => {
    enqueueSnackbar(
      'File is too big or has an invalid format. Only .xlsx files under 10MB are allowed.',
      { variant: 'errorToast' },
    );
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) {
        handleFileRejected();
        setSelectedFile(null);
        setUploadStatus('error');
        return;
      }

      const file = acceptedFiles[0];
      const isValidFormat = file.name.endsWith('.xlsx');
      const isValidSize = file.size <= maxSize;

      if (isValidFormat && isValidSize) {
        setSelectedFile(file);
        setUploadStatus('idle');
      } else {
        handleFileRejected();
        setSelectedFile(null);
        setUploadStatus('error');
      }
    },
    [maxSize],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxSize,
  });

  const handleUpload = () => {
    if (!selectedFile) return;

    setUploadStatus('uploading');
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prevProgress) => {
        const newProgress = prevProgress + 10;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setUploadStatus('success');
            onClose();
          }, 500);
          return 100;
        }
        return newProgress;
      });
    }, 200);

    const formData = new FormData();
    formData.append('file', selectedFile);

    $authHost
      .post('/affiliate-lead/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(progress);
          }
        },
      })
      .then((response) => {
        clearInterval(interval);
        setUploadStatus('success');
        enqueueSnackbar(response.data?.data?.message || 'Import successful', {
          variant: 'successToast',
          className: 'h-fit',
        });
        setSelectedFile(null);
        onClose();
        onUploadSuccess?.();
      })
      .catch((error) => {
        clearInterval(interval);
        setUploadStatus('error');
        enqueueSnackbar(
          `Sorry, something went wrong. ${
            error?.response?.data?.message || error.message || 'Unknown error'
          }`,
          {
            variant: 'errorToast',
          },
        );
      });
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploadStatus('idle');
    onClose();
  };

  return (
    <AppSimpleDialog open={open} handleClose={onClose} title="Upload" size="sm" scroll="paper">
      <Divider />
      <DialogContent className="px-24 relative">
        <Box
          {...getRootProps()}
          className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:bg-gray-50"
        >
          <FuseSvgIcon className="flex justify-center mb-16 text-gray-700" size={48}>
            material-outline:note_add
          </FuseSvgIcon>
          <input {...getInputProps()} />
          <Typography variant="body1" className="text-gray-700">
            Select Excel file to upload or drag-and-drop your file here
          </Typography>
          <Typography variant="caption" className="text-gray-500">
            Maximum file size 10MB.
          </Typography>
        </Box>
        {selectedFile && (
          <Box className="mt-40 flex items-start justify-between p-4 rounded-md bg-gray-50 py-8">
            <Box className="flex items-top gap-8">
              <Box height={24} className="text-gray-400">
                <Icon>insert_drive_file</Icon>
              </Box>
              <Box className="flex flex-col ml-2 mr-2">
                <Typography variant="body2">{selectedFile.name}</Typography>
                <Typography variant="caption" className="text-gray-500">
                  {Math.round(selectedFile.size / 1024)} KB
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center">
              <CheckIcon aria-label="remove" className="text-green-500" />
              <IconButton
                onClick={() => {
                  setSelectedFile(null);
                  setUploadStatus('idle');
                }}
                aria-label="remove"
                className="text-gray-500 hover:text-gray-700"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        )}
        <DialogActions className="flex justify-end mr-24 ml-12 my-20 p-0">
          <Button
            onClick={handleCancel}
            className="flex items-center text-gray-600 font-500 bg-gray-50 hover:bg-gray-200 px-16 py-8"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || uploadStatus === 'uploading'}
            className="ml-16 text-white font-500 bg-blue-primary hover:bg-blue-primary-hover px-16 py-8 disabled:bg-gray-400 disabled:text-opacity-60"
          >
            Upload
          </Button>
        </DialogActions>
      </DialogContent>
    </AppSimpleDialog>
  );
};

export default FileUploadDialog;
